import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { toast } from 'react-toastify';
import FormLabel from 'components/form-label';
import { PAYMENT_CONFIG, PaymentDeviceConfig, ModalType } from 'state/paymentConfig/type';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import FullDeviceConfig from '../fullDeviceConfig';
import SimpleDeviceConfig from '../simpleDeviceConfig';
import { useForm } from 'react-hook-form';
interface IProps {
  modal: boolean;
  setModal: (show: boolean, type?: ModalType) => void;
  type: ModalType;

  submitCall: (
    name:
      | PAYMENT_CONFIG.ICreatePaymentDeviceSimple
      | PAYMENT_CONFIG.ICreatePaymentDeviceFull,
    id?: string
  ) => void;
  processorId: string;
  deviceId: string | undefined;
  deviceConfigType: PaymentDeviceConfig;
  deviceConfig: Record<string, string | undefined | null>;
  setDeviceConfig: Dispatch<SetStateAction<Record<string, string | undefined | null>>>;
  posLocationList: PAYMENT_CONFIG.IPosLocation[] | undefined;
}

export const encryptedValueSetter = (value: string, compareString: string) => {
  if (value === `%${compareString}%`) {
    return undefined;
  } else if (value === '') {
    return null;
  } else {
    return value;
  }
};

export const POS_LOCATION_NONE = '__none__';

// Derive the controlled Select value from device config. A gateway-type POS
// location is stored in `locationId` (selltreez/other in `posLocationId`), so
// both must be considered — otherwise the trigger snaps back to "none" right
// after a gateway location is selected.
export const getPosLocationSelectValue = (
  deviceConfig: Record<string, string | undefined | null>
): string =>
  deviceConfig.posLocationId || deviceConfig.locationId || POS_LOCATION_NONE;

export const applyPosLocationChange = (
  prev: Record<string, string | undefined | null>,
  selectedItemId: string,
  posLocationList: PAYMENT_CONFIG.IPosLocation[] | undefined
): Record<string, string | undefined | null> => {
  if (selectedItemId === POS_LOCATION_NONE) {
    return { ...prev, posLocationId: null, locationId: null };
  }

  const selectedItem = posLocationList?.find((item) => item.id === selectedItemId);

  if (!selectedItem) {
    return { ...prev, posLocationId: null, locationId: null };
  }

  if (selectedItem.type === 'gateway') {
    return { ...prev, posLocationId: undefined, locationId: selectedItem.id };
  }
  if (selectedItem.type === 'selltreez') {
    return { ...prev, posLocationId: selectedItem.id, locationId: null };
  }
  return { ...prev, posLocationId: selectedItemId, locationId: null };
};

const AddPaymentDevice = (props: IProps): JSX.Element => {
  const {
    modal,
    setModal,
    type,
    submitCall,
    processorId,
    deviceId,
    deviceConfigType,
    deviceConfig,
    setDeviceConfig,
    posLocationList,
  } = props;

  const [disabledButton, setDisabledButton] = useState(true);

  const { paymentConfig } = usePaymentConfig();
  const errorFromRedux =
    type === ModalType.EDIT
      ? paymentConfig?.error?.editPaymentDevice?.message
      : paymentConfig?.error?.createPaymentDevice?.message;

  // Surface backend failures via a toast so the message is visible above the
  // open modal. The toast fires once per error change.
  useEffect(() => {
    if (errorFromRedux && modal) {
      toast.error(errorFromRedux);
    }
  }, [errorFromRedux, modal]);

  const { getValues, register, setValue } = useForm();

  const editDeviceFunction = () => {
    if (deviceConfig.deviceName?.length !== 0) {
      submitCall(
        {
          deviceregisterid: deviceConfig.registerId || '',
          name: deviceConfig.deviceName || '',
          processorId: processorId,
          deviceauthcode:
            encryptedValueSetter(
              getValues('authCode'),
              deviceConfig.registerId || ''
            ) || null,
          printConfig:
            deviceConfig.printConfig !== 'NONE'
              ? deviceConfig.printConfig || null
              : null,
          tpn: deviceConfig?.tpn || undefined,
          posLocationId: deviceConfig?.posLocationId || undefined,
          locationId: deviceConfig?.locationId || undefined,
        },
        deviceId
      );
    }
  };
  const addDeviceFunction = () => {
    if (deviceConfig.deviceName?.length !== 0) {
      submitCall(
        {
          deviceregisterid: deviceConfig.registerId || '',
          name: deviceConfig.deviceName || '',
          processorId: processorId,
          posLocationId: deviceConfig?.posLocationId || '',
          locationId: deviceConfig?.locationId || '',
        },
        deviceId
      );
    }
  };
  useEffect(() => {
    if (deviceConfig?.deviceName && deviceConfig?.registerId) {
      setDisabledButton(false);
    } else {
      setDisabledButton(true);
    }
  }, [deviceConfig]);

  return (
    <Dialog open={modal} onOpenChange={setModal}>
      <DialogContent className="max-w-xl" data-testid="add-payment-device-modal">
        {type === ModalType.SAVE && (
          <>
            <div className="space-y-4">
              <span className="text-base font-semibold text-gray-blue-90">
                Add a new payment device
              </span>
              <form role="form" className="space-y-4">
                <div>
                  <FormLabel
                    element="device_name"
                    label="Name of device"
                    className="text-sm font-medium text-gray-blue-80 mb-1"
                  />
                  <Input
                    name="device_name"
                    id="device_name"
                    className="w-full bg-gray-blue-1"
                    onChange={(e) => {
                      const { value } = e.currentTarget;
                      setDeviceConfig((prev) => ({ ...prev, deviceName: value }));
                    }}
                    defaultValue={deviceConfig.deviceName || ''}
                  />
                </div>
                <div>
                  <FormLabel
                    element="pos_location"
                    label="POS Location"
                    className="text-sm font-medium text-gray-blue-80 mb-1"
                  />
                  <Select
                    value={getPosLocationSelectValue(deviceConfig)}
                    onValueChange={(selectedItemId) => {
                      setDeviceConfig((prev) =>
                        applyPosLocationChange(prev, selectedItemId, posLocationList)
                      );
                    }}
                  >
                    <SelectTrigger id="pos_location" className="w-full bg-gray-blue-1">
                      <SelectValue placeholder="none" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={POS_LOCATION_NONE}>none</SelectItem>
                      {posLocationList?.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FormLabel
                    element="register_id"
                    label="Register Id"
                    className="text-sm font-medium text-gray-blue-80 mb-1"
                  />
                  <Input
                    name="register_id"
                    id="register_id"
                    className="w-full bg-gray-blue-1"
                    onChange={(e) => {
                      const { value } = e.currentTarget;
                      setDeviceConfig((prev) => ({ ...prev, registerId: value }));
                    }}
                    defaultValue={deviceConfig.registerId || ''}
                  />
                </div>
              </form>
            </div>
            <DialogFooter className="flex justify-end gap-2">
              <Button onClick={() => setModal(false)} variant="secondary">
                Cancel
              </Button>
              <Button
                data-testid="paymentDeviceCTA"
                onClick={addDeviceFunction}
                disabled={disabledButton}
              >
                Add payment device
              </Button>
            </DialogFooter>
          </>
        )}
        {type === ModalType.EDIT && (
          <>
            <div className="space-y-4">
              <span className="text-base font-semibold text-gray-blue-90">
                Edit payment device
              </span>
              <div className="space-y-4">
                <div>
                  <FormLabel
                    element="device_name"
                    label="Name of device"
                    className="text-sm font-medium text-gray-blue-80 mb-1"
                  />
                  <Input
                    name="device_name"
                    id="device_name"
                    className="w-full bg-gray-blue-1"
                    onChange={(e) => {
                      const { value } = e.currentTarget;
                      setDeviceConfig((prev) => ({ ...prev, deviceName: value }));
                    }}
                    defaultValue={deviceConfig.deviceName || ''}
                  />
                </div>
                <div>
                  <FormLabel
                    element="pos_location"
                    label="POS Location"
                    className="text-sm font-medium text-gray-blue-80 mb-1"
                  />
                  <Select
                    value={getPosLocationSelectValue(deviceConfig)}
                    onValueChange={(selectedItemId) => {
                      setDeviceConfig((prev) =>
                        applyPosLocationChange(prev, selectedItemId, posLocationList)
                      );
                    }}
                  >
                    <SelectTrigger id="pos_location" className="w-full bg-gray-blue-1">
                      <SelectValue placeholder="none" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={POS_LOCATION_NONE}>none</SelectItem>
                      {posLocationList?.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {deviceConfigType === PaymentDeviceConfig.SIMPLE && (
                  <SimpleDeviceConfig
                    setDeviceConfig={setDeviceConfig}
                    disabled={false}
                    inModal
                    deviceConfig={deviceConfig}
                  />
                )}
                {deviceConfigType == PaymentDeviceConfig.FULL && (
                  <FullDeviceConfig
                    deviceConfig={deviceConfig}
                    setDeviceConfig={setDeviceConfig}
                    disabled={false}
                    register={register}
                    setValue={setValue}
                  />
                )}
              </div>
            </div>
            <DialogFooter className="flex justify-end gap-2">
              <Button onClick={() => setModal(false)} variant="secondary">
                Cancel
              </Button>
              <Button
                data-testid="paymentDeviceCTA"
                onClick={editDeviceFunction}
                disabled={disabledButton}
              >
                Edit payment device
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddPaymentDevice;
